/**
 * WON Block
 * 
 * Pattern recognition: "What worked in your last wins"
 * Shows patterns and "Apply this pattern to active deals" CTA
 */

import { Deal } from '@/hooks/useDeals';
import { Lead } from '@/hooks/useLeads';
import { formatCurrency } from '@/hooks/useUnifiedMetrics';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Trophy, TrendingUp, Clock, Sparkles, ArrowRight } from 'lucide-react';
import { differenceInDays, format } from 'date-fns';
import { useMemo } from 'react';
import { detectWinPatterns } from '@/lib/patternRecognition';
import { useNavigate } from 'react-router-dom';

interface WonBlockProps {
  deals: Deal[];
  leads: Lead[];
}

const WIN_REASON_LABELS: Record<string, string> = {
  'time': 'Time / Urgency',
  'price': 'Price / Value',
  'relationship': 'Relationship / Trust',
  'follow_up': 'Follow-up / Persistence',
  'demo': 'Demo / Presentation',
  'other': 'Other',
};

export function WonBlock({ deals, leads }: WonBlockProps) {
  const navigate = useNavigate();
  
  const wonData = useMemo(() => {
    const now = new Date();
    
    // Filter won deals
    const wonDeals = deals.filter(d => {
      const dealStatus = (d as any).deal_status;
      return dealStatus === 'won' || d.stage === 'won';
    });
    
    // Calculate metrics
    const totalRevenue = wonDeals.reduce((sum, d) => sum + (d.value || 0), 0);
    
    // Last 5 deals
    const lastDeals = wonDeals
      .sort((a, b) => {
        const closedA = (a as any).closed_at ? new Date((a as any).closed_at) : new Date(a.updated_at);
        const closedB = (b as any).closed_at ? new Date((b as any).closed_at) : new Date(b.updated_at);
        return closedB.getTime() - closedA.getTime();
      })
      .slice(0, 5);
    
    // Average closing time (days from creation to close)
    const closingTimes: number[] = [];
    wonDeals.forEach(deal => {
      const closedAt = (deal as any).closed_at ? new Date((deal as any).closed_at) : new Date(deal.updated_at);
      const createdAt = new Date(deal.created_at);
      const days = differenceInDays(closedAt, createdAt);
      if (days >= 0) {
        closingTimes.push(days);
      }
    });
    const avgClosingTime = closingTimes.length > 0
      ? Math.round(closingTimes.reduce((sum, d) => sum + d, 0) / closingTimes.length)
      : 0;
    
    // Detect patterns
    const patterns = detectWinPatterns(wonDeals, leads);
    
    return {
      totalRevenue,
      count: wonDeals.length,
      lastDeals,
      avgClosingTime,
      patterns,
    };
  }, [deals, leads]);
  
  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-2 h-2 rounded-full bg-success" />
          <h2 className="text-xl font-bold text-foreground">WON</h2>
          <Badge variant="secondary" className="text-xs font-mono bg-success/10 text-success">
            {wonData.count}
          </Badge>
        </div>
        <div className="text-2xl font-bold text-success">
          {formatCurrency(wonData.totalRevenue)}
        </div>
      </div>
      
      {/* What worked section */}
      {wonData.patterns.length > 0 && (
        <div className="space-y-3">
          <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-success" />
            What worked in your last wins
          </h3>
          <div className="space-y-2">
            {wonData.patterns.map((pattern, idx) => (
              <Card key={idx} className="border-success/20 bg-success/5">
                <CardContent className="p-3">
                  <div className="flex items-center justify-between">
                    <p className="text-sm text-foreground">{pattern.description}</p>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => navigate('/app/today')}
                      className="h-7 px-2 text-xs gap-1"
                    >
                      Apply pattern
                      <ArrowRight className="w-3 h-3" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}
      
      {/* Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Total Revenue */}
        <Card className="border-success/20 bg-success/5">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-success/10 flex items-center justify-center">
                <Trophy className="w-5 h-5 text-success" />
              </div>
              <div>
                <p className="text-2xl font-bold text-success">
                  {formatCurrency(wonData.totalRevenue)}
                </p>
                <p className="text-xs text-muted-foreground">Total Revenue</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        {/* Average Closing Time */}
        {wonData.avgClosingTime > 0 && (
          <Card className="border-border/40 bg-card">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                  <Clock className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-foreground">
                    {wonData.avgClosingTime}
                  </p>
                  <p className="text-xs text-muted-foreground">Avg days to close</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
      
      {/* Last Deals */}
      {wonData.lastDeals.length > 0 && (
        <div className="space-y-2">
          <h3 className="text-sm font-medium text-muted-foreground">Recent Wins</h3>
          <div className="space-y-2">
            {wonData.lastDeals.map((deal) => {
              const lead = leads.find(l => l.id === deal.lead_id);
              const closedAt = (deal as any).closed_at ? new Date((deal as any).closed_at) : new Date(deal.updated_at);
              const winReason = (deal as any).win_reason || 'other';
              const reasonLabel = WIN_REASON_LABELS[winReason] || 'Other';
              const hasPattern = winReason && winReason !== 'other';
              
              return (
                <Card key={deal.id} className="border-success/10 bg-success/5">
                  <CardContent className="p-3">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <p className="font-medium text-foreground">
                            {lead?.name || deal.lead?.name || 'Unknown'}
                          </p>
                          <Badge variant="outline" className="text-[10px] px-1.5 py-0 border-success/30 text-success bg-success/10">
                            {reasonLabel}
                          </Badge>
                          {hasPattern && (
                            <Badge variant="outline" className="text-[10px] px-1.5 py-0 border-success/30 text-success bg-success/10">
                              Pattern detected
                            </Badge>
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground mb-1">
                          Won on {format(closedAt, 'MMM d, yyyy')}
                        </p>
                        <p className="text-xs text-muted-foreground/70 italic">
                          What worked: {reasonLabel}
                        </p>
                      </div>
                      <div className="text-lg font-bold text-success">
                        {formatCurrency(deal.value || 0)}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      )}
      
      {wonData.count === 0 && (
        <div className="text-center py-12 text-muted-foreground text-sm">
          No deals won yet
        </div>
      )}
    </div>
  );
}

